import { useNavigate, useSearch } from "@tanstack/react-router";
import { Icon } from "@/deps/ui-library";
import { useFundTransfers } from "@/api/queries";
import type { FundTransfer } from "@/api/types";
import { PermittedAction } from "@/components/PermittedAction";
import type { Column } from "@/components/list/DataTable";
import { DocumentStatusBadge } from "@/components/list/StatusBadge";
import type { ListSearch } from "@/components/list/listQuery";
import { ListScreen, listQueryFor } from "@/components/list/ListScreen";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";

export function FundTransfersListPage() {
  const search = useSearch({ from: "/banking/transfers" }) as ListSearch;
  const navigate = useNavigate();
  const { data, isPending, error, refetch } = useFundTransfers(listQueryFor(search));

  const columns: Array<Column<FundTransfer>> = [
    {
      key: "number",
      header: "Number",
      width: "11ch",
      numeric: true,
      render: (row) => row.number ?? "Draft",
    },
    { key: "date", header: "Date", width: "12ch", render: (row) => formatDate(row.date) },
    {
      key: "route",
      header: "From and to",
      render: (row) => (
        <span className="flex items-center gap-1.5">
          <span className="text-fg">{row.fromName}</span>
          <Icon name="ArrowRight" size="sm" />
          <span className="text-fg">{row.toName}</span>
        </span>
      ),
    },
    { key: "reference", header: "Reference", render: (row) => row.reference },
    {
      key: "status",
      header: "Status",
      width: "14ch",
      render: (row) => <DocumentStatusBadge status={row.status} />,
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      numeric: true,
      render: (row) => formatAmount(row.amount),
    },
  ];

  return (
    <ListScreen
      route="/banking/transfers"
      title="Fund transfers"
      subtitle={data ? `${data.total} this fiscal year` : "Loading transfers"}
      actions={
        <PermittedAction
          variant="secondary"
          leftIcon="Plus"
          permission="banking.transfers.create"
          onClick={() => void navigate({ to: "/banking/transfers/new" })}
        >
          New transfer
        </PermittedAction>
      }
      columns={columns}
      rows={data?.items ?? []}
      total={data?.total ?? 0}
      hasNext={data?.next != null}
      loading={isPending}
      error={error}
      onRetry={() => void refetch()}
      rowKey={(row) => row.id}
      rowLabel={(row) =>
        `Transfer ${row.number ?? "draft"}, ${row.fromName} to ${row.toName}, ${formatAmount(row.amount)}`
      }
      onRowClick={(row) => void navigate({ to: "/banking/transfers/$id", params: { id: row.id } })}
      searchPlaceholder="Search number, account or reference"
      // A transfer between your own accounts has no approval ladder and no payment state.
      statuses={null}
      empty={{
        icon: "ArrowLeftRight",
        title: "No fund transfers yet",
        description:
          "A transfer moves money between your own accounts. It changes where the cash is, never how much there is.",
      }}
      filteredEmpty={{
        icon: "Search",
        title: "No transfers match these filters",
        description: "Try a wider date range, or clear the filters to see everything again.",
      }}
    />
  );
}
